import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Heading,
  Text,
  Stack,
  useToast,
  useColorModeValue,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Checkbox,
  CheckboxGroup,
  SimpleGrid,
  Select,
  Switch,
  Radio,
  RadioGroup,
  Collapse,
  Tooltip,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useContext, useEffect } from 'react';
import { HttpContext } from '../../context/HttpContext';
import CustomToast from '../../common/CustomToast';
import { getErrors } from '../../utils/ajax';
import { MeContext } from '../../context/MeContext';
import { CustomExclamationMarkInverseIcon } from '../../theme/icons/CustomExclamationMarkInverseIcon';

const EVENT_OPTIONS = [
  { label: 'Pass created', value: 'passCreated' },
  { label: 'Pass installed', value: 'passInstalled' },
  { label: 'Pass removed', value: 'passRemoved' },
  { label: 'Pass scanned', value: 'passScanned' },
  { label: 'Form submitted', value: 'formSubmitted' },
];

export default function WebhookSubscriptionModal({
  isOpen,
  onClose,
  initialData = null,
  fetchData,
}) {
  const modeBoxBg = useColorModeValue('white', 'secondaryDark');
  const toast = useToast();
  const { authAxios } = useContext(HttpContext);
  const bgTooltip = useColorModeValue('secondaryDark', '#dddddd');
  const textTooltip = useColorModeValue('#ffffff', 'primaryDark');

  const meCtx = useContext(MeContext);
  const {
    entities,
    account: { role },
  } = meCtx.state;

  const schema = Yup.object().shape({
    url: Yup.string().url('Must be a valid URL').required('URL is required'),
    events: Yup.array().of(Yup.string()).min(1, 'Select at least one event'),
    entityUuid: Yup.string().nullable(),
    enabled: Yup.boolean(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
    control,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      url: initialData?.url || '',
      events: initialData?.events || [],
      enabled: initialData ? initialData.enabled : true,
      entityUuid: initialData?.entityUuid || '',
    },
  });

  const scopeType = watch('scopeType');

  useEffect(() => {
    reset({
      url: initialData?.url || '',
      events: initialData?.events || [],
      enabled: initialData ? initialData.enabled : true,
      scopeType: initialData?.scopeType || 'organisation',
      entityUuid: '',
    });
  }, [initialData, reset]);

  const onSubmit = async (data) => {
    const payload = {
      webhookSubscription: {
        url: data.url.trim(),
        subscribedEvents: data.events,
        enabled: data.enabled,
      },
    };
    if (!initialData && data.entityUuid)
      payload.webhookSubscription.entityId = data.entityUuid;

    try {
      if (initialData) {
        await authAxios.patch(
          `/api/v1/webhook-subscriptions/${initialData.id}`,
          payload
        );
      } else {
        await authAxios.post('/api/v1/webhook-subscriptions', payload);
      }
      toast({
        render: ({ onClose: closeToast }) => (
          <CustomToast
            status="success"
            title={`Webhook subscription ${initialData ? 'updated' : 'created'}`}
            onClose={closeToast}
          />
        ),
      });
      onClose();
      fetchData?.({ pageIndex: 0, pageSize: 5 });
    } catch (error) {
      const errors = getErrors(error);
      errors.forEach((err) => {
        toast({
          render: ({ onClose: closeToast }) => (
            <CustomToast
              status="error"
              title={err || 'Something went wrong'}
              onClose={closeToast}
            />
          ),
        });
      });
    }
  };

  return (
    <Modal onClose={onClose} isOpen={isOpen} size="xl">
      <ModalOverlay />
      <ModalContent
        bg={modeBoxBg}
        as="form"
        onSubmit={handleSubmit(onSubmit)}
        px="4"
        py="2"
      >
        <ModalHeader>
          <Heading size="xl">
            {initialData
              ? 'Edit webhook subscription'
              : 'Add webhook subscription'}
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {/* Enabled */}
          <FormControl mb={5} display="flex" alignItems="center">
            <Switch id="enabled" {...register('enabled')} mr={2} />
            <FormLabel htmlFor="enabled" mb="0">
              Enable endpoint
            </FormLabel>
          </FormControl>

          {/* URL */}
          <FormControl isInvalid={!!errors.url} mb={5}>
            <FormLabel>Target URL</FormLabel>
            <Input
              placeholder="https://example.com/webhook"
              {...register('url')}
            />
            <FormErrorMessage mt={1}>{errors.url?.message}</FormErrorMessage>
          </FormControl>

          {/* Events */}
          <FormControl isInvalid={!!errors.events} mb={5}>
            <FormLabel>Events</FormLabel>
            <CheckboxGroup
              colorScheme="brand"
              value={watch('events')}
              onChange={(vals) => setValue('events', vals)}
            >
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                {EVENT_OPTIONS.map((opt) => (
                  <Checkbox key={opt.value} value={opt.value}>
                    {opt.label}
                  </Checkbox>
                ))}
              </SimpleGrid>
            </CheckboxGroup>
            <FormErrorMessage mt={1}>{errors.events?.message}</FormErrorMessage>
          </FormControl>

          {/* Scope (optional) */}
          {entities.length > 1 && role === 'org_admin' && (
            <FormControl mb={5}>
              {initialData ? (
                <Tooltip
                  label="Scope can’t be changed for an existing webhook subscription. To switch scope (e.g. organisation vs entity), please create a new subscription."
                  hasArrow
                  placement="top"
                  fontSize="12px"
                  bg={bgTooltip}
                  color={textTooltip}
                  w="205px"
                  borderRadius="6px"
                  textAlign="center"
                  p="10px"
                >
                  <FormLabel>
                    Scope{' '}
                    <CustomExclamationMarkInverseIcon
                      boxSize="14px"
                      mode={'light'}
                    />
                  </FormLabel>
                </Tooltip>
              ) : (
                <FormLabel>Scope</FormLabel>
              )}

              <Controller
                name="scopeType"
                control={control}
                defaultValue="organisation"
                render={({ field }) => (
                  <RadioGroup
                    {...field}
                    onChange={(val) => {
                      field.onChange(val);
                      if (val === 'organisation') setValue('entityUuid', '');
                    }}
                    isDisabled={!!initialData}
                  >
                    <Stack direction="row">
                      <Radio value="organisation">Organisation</Radio>
                      <Radio value="entity">Entity</Radio>
                    </Stack>
                  </RadioGroup>
                )}
              />

              <Collapse in={scopeType === 'entity' && !initialData}>
                <Select
                  placeholder="Select entity"
                  mt={3}
                  {...register('entityUuid', { required: true })}
                >
                  {entities.map((e) => (
                    <option key={e.uuid} value={e.uuid}>
                      {e.name}
                    </option>
                  ))}
                </Select>
              </Collapse>
            </FormControl>
          )}
        </ModalBody>
        <ModalFooter>
          <Stack direction={{ base: 'column', sm: 'row' }} w="full" spacing={3}>
            <Button type="submit" isLoading={isSubmitting} size="sm">
              {initialData ? 'Save changes' : 'Create webhook'}
            </Button>
            <Button variant="secondary" onClick={onClose} size="sm">
              <Text>Cancel</Text>
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
